import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Siren, Loader2, Filter, X, Users, Building2, HardDrive, UserPlus, Network } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AddCustomer } from '@/features/Customers/AddCustomer';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useAlertSearch } from '@/app/AlertsDashboard/hooks/useAlertSearch';
import { format, isBefore } from 'date-fns';
import { cn } from '@/lib/utils';
import { Time } from '@/components/ui/time';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dateFilter, setDateFilter] = useState<'before' | 'after' | 'between'>('after');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Calculate effective date range based on filter type
  const effectiveStartDate = useMemo(() => {
    if (dateFilter === 'after' || dateFilter === 'between') return startDate;
    return undefined;
  }, [dateFilter, startDate]);

  const effectiveEndDate = useMemo(() => {
    if (dateFilter === 'before') return startDate; // Use startDate as the "before" date
    if (dateFilter === 'between') return endDate;
    return undefined;
  }, [dateFilter, startDate, endDate]);

  // Use the actual alert search hook
  const { data: searchResults, isLoading } = useAlertSearch({
    searchText: query,
    searchType: 'alert',
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
  });

  const results = useMemo(() => searchResults?.alerts || [], [searchResults?.alerts]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Action handlers for command palette items
  const handleAction = useCallback((action: string) => {
    onOpenChange(false);
    setQuery('');
    
    switch (action) {
      case 'add-customer':
        setShowAddCustomer(true);
        break;
      case 'add-site':
        navigate('/site/new');
        break;
      case 'add-device':
        navigate('/devices/add-device');
        break;
      case 'add-vms':
        navigate('/connections/add-connection?connectionType=4'); // Update with correct VMS route
        break;
      case 'add-external-service':
        navigate('/connections/add-connection?connectionType=3'); // Update with correct external service route
        break;
      case 'add-user':
        navigate('/users/add');
        break;
      case 'add-team':
        navigate('/team/add');
        break;
      default:
        console.log('Unknown action:', action);
    }
  }, [navigate, onOpenChange]);


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelectAlert(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  const handleSelectAlert = (alert: (typeof results)[0]) => {
    window.location.href = `/alert/${alert.id}`;
    onOpenChange(false);
    setQuery('');
  };

  const handleClearFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setDateFilter('after');
  };

  const hasActiveFilters = startDate || endDate;

  // Generate filter description for pill
  const getFilterDescription = () => {
    if (!hasActiveFilters) return null;

    if (dateFilter === 'after' && startDate) {
      return `After ${format(startDate, 'MMM d, yyyy')}`;
    }
    if (dateFilter === 'before' && startDate) {
      return `Before ${format(startDate, 'MMM d, yyyy')}`;
    }
    if (dateFilter === 'between' && startDate && endDate) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    if (dateFilter === 'between' && startDate && !endDate) {
      return `After ${format(startDate, 'MMM d, yyyy')}`;
    }
    return 'Date filtered';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-gray-800 bg-[#111] p-0 max-h-[80vh] ">
        <div className="flex items-center border-b bg-[#111]/60 px-4">
          <Search className="h-8 w-8 text-lime" />
          <Input
            ref={inputRef}
            placeholder="Search alerts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="border-0 bg-transparent py-8 text-xl font-semibold text-white placeholder-gray-400 outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <Accordion type="single" collapsible className="">
          <AccordionItem value="filters" className="border-b">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-lime" />
                  <span>
                    {hasActiveFilters ? `Filtering by ${getFilterDescription()}` : 'Filter'}
                  </span>
                </div>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearFilters();
                    }}
                    className="rounded-full border px-4 text-lime text-muted-foreground hover:bg-gray-800/50 hover:text-white"
                  >
                    Clear Filter <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="border-t px-0 pb-0">
              <Tabs
                value={dateFilter}
                onValueChange={(value) => setDateFilter(value as 'before' | 'after' | 'between')}
              >
                <TabsList className="grid w-full grid-cols-3 rounded-none border-0 border-b">
                  <TabsTrigger value="after">After</TabsTrigger>
                  <TabsTrigger value="before">Before</TabsTrigger>
                  <TabsTrigger value="between">Between</TabsTrigger>
                </TabsList>

                <TabsContent value="after" className="p-4">
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Show alerts after:</label>
                    <DateTimePicker
                      value={startDate}
                      onChange={setStartDate}
                      granularity="second"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="before" className="p-4">
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Show alerts before:</label>
                    <DateTimePicker
                      value={startDate}
                      onChange={setStartDate}
                      granularity="second"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="between" className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Start date:</label>
                      <DateTimePicker
                        value={startDate}
                        onChange={(date) => {
                          setStartDate(date);
                          if (date && endDate && isBefore(endDate, date)) {
                            setEndDate(undefined);
                          }
                        }}
                        granularity="second"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">End date:</label>
                      <DateTimePicker
                        value={endDate}
                        onChange={(date) => {
                          if (date && startDate && !isBefore(date, startDate)) {
                            setEndDate(date);
                          } else if (!startDate) {
                            setEndDate(date);
                          }
                        }}
                        disabled={!startDate}
                        granularity="second"
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="max-h-[calc(80vh-20rem)] overflow-y-scroll">
          {isLoading && (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin text-lime" />
              <span className="ml-2">Searching...</span>
            </div>
          )}

          {!isLoading && query && results.length === 0 && (
            <div className="flex items-center justify-center py-8 text-gray-400">
              No alerts found
            </div>
          )}

          {!isLoading && results.length > 0 && (
            <div className="py-2">
              {results.map((alert, index) => (
                <div
                  key={alert.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors',
                    index === selectedIndex
                      ? 'border-l-2 border-lime bg-lime/10'
                      : 'hover:bg-gray-800/50'
                  )}
                  onClick={() => handleSelectAlert(alert)}
                >
                  <Siren className="h-4 w-4 flex-shrink-0 text-lime" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-lime">{alert.alertId}</span>
                      <span className="text-xs text-muted-foreground">
                        {alert.occurred && <Time date={alert.occurred} noTooltip />}
                      </span>
                    </div>
                    <p className="truncate text-sm text-gray-300">
                      {alert.companyName} - {alert.projectName}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!query && (
            <div className="flex flex-col items-start text-gray-400">
              <h3 className="p-2 pb-1 text-sm font-semibold">Quick Add</h3>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-customer')}
              >
                <div className="flex items-center gap-2">
                  <Users className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add Customer</p>
                </div>
              </button>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-site')}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add Site</p>
                </div>
              </button>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-device')}
              >
                <div className="flex items-center gap-2">
                  <HardDrive className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add Device</p>
                </div>
              </button>
              <h3 className="p-2 pb-1 text-sm font-semibold">Connections</h3>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-vms')}
              >
                <div className="flex items-center gap-2">
                  <Network className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add VMS</p>
                </div>
              </button>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-external-service')}
              >
                <div className="flex items-center gap-2">
                  <Network className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add External Service</p>
                </div>
              </button>
              <h3 className="p-2 pb-1 text-sm font-semibold">Admin</h3>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-user')}
              >
                <div className="flex items-center gap-2">
                  <UserPlus className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add User</p>
                </div>
              </button>
              <button 
                className="flex w-full gap-2 p-2 py-3 text-lime hover:bg-gray-800/50 transition-colors cursor-pointer"
                onClick={() => handleAction('add-team')}
              >
                <div className="flex items-center gap-2">
                  <Users className="mx-auto h-6 w-6" />
                  <p className="font-semibold">Add Team</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </DialogContent>
      </Dialog>
      
      <AddCustomer 
        open={showAddCustomer} 
        onOpenChange={setShowAddCustomer}
      />
    </>
  );
}
